import { computed, action, makeObservable } from 'mobx'
import { types as t, Instance } from '@invisr/mobx-state-tree'
import { uistore } from '../uistore'
import crudActions from './crud-actions'
import DataSourceType, { WithDS } from './datasource-type'
import type { History } from 'history'

const TARGET_BLANK = '_blank'
const STATE_LABELS = [
  {
    key: 'history',
    label: 'history',
    fieldType: 'object',
    meta: { fieldPath: 'history' },
    childNodes: [
      {
        key: 'location',
        label: 'location',
        fieldType: 'object',
        meta: { fieldPath: 'location' },
        childNodes: [
          { key: 'key', label: 'key', fieldType: 'string', meta: { fieldPath: 'key' } },
          { key: 'pathname', label: 'pathname', fieldType: 'string', meta: { fieldPath: 'pathname' } },
          { key: 'search', label: 'search', fieldType: 'string', meta: { fieldPath: 'search' } },
          { key: 'hash', label: 'hash', fieldType: 'string', meta: { fieldPath: 'hash' } },
          { key: 'state', label: 'state', fieldType: 'object', meta: { fieldPath: 'state' } }
        ]
      }
    ]
  }
]

export const Params = t
  .model('Params', {
    name: t.maybeNull(t.string),
    value: t.maybeNull(t.string)
  })
  .actions(crudActions)

const ClientRoutesConfig = 
  t.model('ClientRoutesConfig', {
    type: t.optional(t.string, 'Client Routes'),
    items: t.optional(t.array(Params), [])
  })
  .views(self => ({
    get provider () {
      return ClientRoutesProvider
    },
    fieldPath (node) {
      return node.meta && node.meta.fieldPath
    },
    get icon () {
      return 'route'
    },
    labels () {
      const p = new ClientRoutesProvider(self)
      const s = self as WithDS<typeof self>
      return {
        key: s.id,
        label: s.name,
        childNodes: [
          {
            key: 'data',
            label: 'Data',
            isExpanded: false,
            childNodes: self.items.map(item => ({
              key: item.name,
              label: item.name,
              secondaryLabel: item.value,
              meta: { fieldPath: item.value }
            }))
          },
          {
            key: 'methods',
            label: 'Methods',
            isExpanded: false,
            childNodes: Object.keys(p)
              .filter(k => typeof p[k] === 'function')
              .map(f => ({
                key: f,
                label: f,
                secondaryLabel: 'FUNCTION',
                meta: { fieldPath: f }
              }))
          },
          {
            key: 'state',
            label: 'State',
            meta: { fieldPath: '$' },
            childNodes: STATE_LABELS
          }
        ]
      }
    }
  }))
  .actions(self => ({
    update (key, val) {
      self[key] = val
    },
    addItem (item = {}) {
      self.items.push({})
    },
    removeItem (item) {
      self.items.splice(self.items.indexOf(item), 1)
    },
    updateItemAtIndex (index, key, value) {
      self.items[index][key] = value
    }
  }))

const Config = t.compose(ClientRoutesConfig, DataSourceType)

export default Config

export class ClientRoutesProvider {
  config: Instance<typeof Config>

  constructor (config) {
    makeObservable(this, {
      history: computed,
      data: computed,
      query: action
    })

    this.config = config
  }

  get history () : History | null {
    return uistore.router?.history || null;
  }

  get data () {
    const { params, match, location } = uistore.router || {}
    const _params = (match && match.params) || params || {}
    const data = { _query: {}, ..._params }
    if (location?.search) {
      const q = new URLSearchParams(location.search)
      for (const [k, v] of Array.from(q.entries())) {
        data._query[k] = v
      }
    }
    this.config.items.forEach(({ value = '' }) => {
      if (_params[value]) data[value] = _params[value]
      else data[value] = ''
    })
    return data
  }

  reload = context => {
    uistore.dataSourceFactory &&
      uistore.dataSourceFactory.reloadData(null, context)
  }

  go = async places => {
    if (this.history && this.history.go) {
      this.history.go(places)
    }
  }

  goto = async (route, state = {}) => {
    if (typeof route === 'object' && !Array.isArray(route) && route.state?.target === TARGET_BLANK) {
      if (!route.pathname) return
      const url = (new URL(route.pathname, window.location.href)).href
      return window.open(url, TARGET_BLANK)
    }
    if (!this.history) return
    this.history.push(route, state)
  }

  back = async () => {
    if (this.history && this.history.goBack) {
      this.history.goBack()
    }
  }

  forward = async () => {
    if (this.history && this.history.goForward) {
      this.history.goForward()
    }
  }

  query = async context => {
    /* let data = {}
     * if (!this.history && context && context.history) {
     *   this.history = context.history
     * }
     *
     * let params = (context && context.match && context.match.params) || {}
     * this.config.items.forEach(({ value } = {}) => {
     *   if (params[value]) data[value] = params[value]
     * })

     * runInAction(() => (this.data = { ...this.data, ...data }))
     * return Promise.resolve(this.data) */
  }
}
